import React, { useEffect, useRef, useState, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { BACKEND_NODE_URL } from '../config/constants';
import TextType from './TextType';
import WeatherCard from './WeatherCard';
import FxCard from './FxCard';
import LoadingSpinner from './LoadingSpinner';
import SlashCommandMenu from './SlashCommandMenu';
import './DashboardPage.css';
import AppIcon from './icons/AppIcon';

const DashboardPage = () => {
  const { user } = useAuth();
  const [initialLoading, setInitialLoading] = useState(true);
  const [displayName, setDisplayName] = useState('');
  // Chat com agente Halo
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSessionId, setChatSessionId] = useState(null);
  const chatEndRef = useRef(null);
  const chatTextareaRef = useRef(null);
  // Histórico de sessões
  const [showSessions, setShowSessions] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsOffset, setSessionsOffset] = useState(0);
  const [hasMoreSessions, setHasMoreSessions] = useState(true);
  const sessionsListRef = useRef(null);
  // Media upload inline (áudio/imagem)
  const [activeRecorders, setActiveRecorders] = useState({});
  const mediaRecorderRefs = useRef({});
  const audioChunksRefs = useRef({});
  // QR codes carregados do banco
  const [qrCodes, setQrCodes] = useState({});
  // Menu de ferramentas (slash commands)
  const [showSlashMenu, setShowSlashMenu] = useState(false);
  const [slashFilter, setSlashFilter] = useState('');

  // Carrega nome do perfil (profiles.usuario_nome) SEM fallback
  useEffect(() => {
    let cancelled = false;
    const loadName = async () => {
      try {
        if (!user?.id) return;
        const cacheKey = `astro_profile_${user.id}`;
        const { data, error } = await supabase
          .from('profiles')
          .select('id, nome_usuario')
          .eq('id', user.id)
          .single();
        if (error) return;
        if (!cancelled) {
          const nome = data?.nome_usuario || '';
          setDisplayName(nome);
          try { localStorage.setItem(cacheKey, JSON.stringify(data)); } catch (_) {}
        }
      } catch (_) {}
      finally {
        if (!cancelled) setInitialLoading(false);
      }
    };
    loadName();
    return () => { cancelled = true; };
  }, [user?.id]);
  // Inicializar sessão do chat (vinculada à empresa para isolamento)
  useEffect(() => {
    const companyId = user?.company_id;
    if (!companyId) return;
    const storageKey = `webchat_session_id_${companyId}`;
    let sid = sessionStorage.getItem(storageKey);
    if (!sid) {
      sid = crypto.randomUUID();
      sessionStorage.setItem(storageKey, sid);
    }
    setChatSessionId(sid);
    setChatMessages([]);
  }, [user?.company_id]);

  // Carregar histórico do chat quando sessão estiver pronta
  useEffect(() => {
    if (!chatSessionId) return;
    const loadChatHistory = async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData?.session?.access_token;
        if (!token) return;
        const resp = await fetch(`${BACKEND_NODE_URL}/api/webchat/history/${chatSessionId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (resp.ok) {
          const data = await resp.json();
          if (data.messages?.length > 0) {
            setChatMessages(data.messages.map(m => ({
              role: m.role,
              content: m.content,
              timestamp: m.created_at
            })));
          }
        }
      } catch (err) {
        console.error('[DashboardChat] Erro ao carregar histórico:', err);
      }
    };
    loadChatHistory();
  }, [chatSessionId]);

  // Scroll para baixo quando novas mensagens
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, chatLoading]);

  // Enviar mensagem ao agente
  const sendChatMessage = useCallback(async () => {
    const msg = chatInput.trim();
    if (!msg || chatLoading) return;
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: msg, timestamp: new Date().toISOString() }]);
    setChatLoading(true);
    if (chatTextareaRef.current) {
      chatTextareaRef.current.style.height = 'auto';
    }
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) {
        setChatMessages(prev => [...prev, { role: 'assistant', content: 'Você precisa estar logado para usar o assistente.', timestamp: new Date().toISOString() }]);
        setChatLoading(false);
        return;
      }
      const resp = await fetch(`${BACKEND_NODE_URL}/api/webchat/message`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg, session_id: chatSessionId })
      });
      if (!resp.ok) throw new Error(`Erro ${resp.status}`);
      const data = await resp.json();
      setChatMessages(prev => [...prev, { role: 'assistant', content: data.response, timestamp: data.timestamp }]);
    } catch (err) {
      console.error('[DashboardChat] Erro:', err);
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Desculpe, ocorreu um erro. Tente novamente.', timestamp: new Date().toISOString() }]);
    } finally {
      setChatLoading(false);
      setTimeout(() => { chatTextareaRef.current?.focus(); }, 50);
    }
  }, [chatInput, chatLoading, chatSessionId]);

  const handleChatKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendChatMessage();
    }
  };

  const handleChatInputChange = (e) => {
    const val = e.target.value;
    setChatInput(val);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 100) + 'px';

    // Detectar / para abrir menu automaticamente
    if (val.startsWith('/')) {
      const filter = val.slice(1).trim();
      setSlashFilter(filter);
      if (!showSlashMenu) setShowSlashMenu(true);
    } else if (showSlashMenu && !val.startsWith('/')) {
      setShowSlashMenu(false);
      setSlashFilter('');
    }
  };

  const sendSuggestion = useCallback(async (text) => {
    if (chatLoading) return;
    setChatMessages(prev => [...prev, { role: 'user', content: text, timestamp: new Date().toISOString() }]);
    setChatLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) {
        setChatMessages(prev => [...prev, { role: 'assistant', content: 'Você precisa estar logado para usar o assistente.', timestamp: new Date().toISOString() }]);
        setChatLoading(false);
        return;
      }
      const resp = await fetch(`${BACKEND_NODE_URL}/api/webchat/message`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, session_id: chatSessionId })
      });
      if (!resp.ok) throw new Error(`Erro ${resp.status}`);
      const data = await resp.json();
      setChatMessages(prev => [...prev, { role: 'assistant', content: data.response, timestamp: data.timestamp }]);
    } catch (err) {
      console.error('[DashboardChat] Erro:', err);
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Desculpe, ocorreu um erro. Tente novamente.', timestamp: new Date().toISOString() }]);
    } finally {
      setChatLoading(false);
      setTimeout(() => { chatTextareaRef.current?.focus(); }, 50);
    }
  }, [chatLoading, chatSessionId]);

  const handleNewChatSession = () => {
    const newSid = crypto.randomUUID();
    const companyId = user?.company_id;
    if (companyId) sessionStorage.setItem(`webchat_session_id_${companyId}`, newSid);
    setChatSessionId(newSid);
    setChatMessages([]);
    setShowSessions(false);
  };

  // Carregar sessões do histórico
  const loadSessions = useCallback(async (reset = false) => {
    if (sessionsLoading) return;
    setSessionsLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) return;
      const off = reset ? 0 : sessionsOffset;
      const resp = await fetch(`${BACKEND_NODE_URL}/api/webchat/sessions?limit=10&offset=${off}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        const newSessions = data.sessions || [];
        if (reset) {
          setSessions(newSessions);
          setSessionsOffset(newSessions.length);
        } else {
          setSessions(prev => [...prev, ...newSessions]);
          setSessionsOffset(off + newSessions.length);
        }
        setHasMoreSessions(newSessions.length >= 10);
      }
    } catch (err) {
      console.error('[DashboardChat] Erro ao carregar sessões:', err);
    } finally {
      setSessionsLoading(false);
    }
  }, [sessionsLoading, sessionsOffset]);

  // Abrir/fechar painel de sessões
  const toggleSessions = useCallback(() => {
    const willOpen = !showSessions;
    setShowSessions(willOpen);
    if (willOpen) {
      setSessions([]);
      setSessionsOffset(0);
      setHasMoreSessions(true);
      // Carregar primeiras sessões
      setTimeout(() => {
        loadSessions(true);
      }, 50);
    }
  }, [showSessions]);

  // Scroll infinito nas sessões
  const handleSessionsScroll = useCallback((e) => {
    const el = e.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 50 && hasMoreSessions && !sessionsLoading) {
      loadSessions(false);
    }
  }, [hasMoreSessions, sessionsLoading, loadSessions]);

  // Trocar para uma sessão antiga
  const switchToSession = useCallback((sessionId) => {
    const companyId = user?.company_id;
    if (companyId) sessionStorage.setItem(`webchat_session_id_${companyId}`, sessionId);
    setChatSessionId(sessionId);
    setChatMessages([]);
    setShowSessions(false);
  }, [user?.company_id]);

  // Formatar data relativa
  const formatRelativeDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffMin = Math.floor(diffMs / 60000);
      const diffH = Math.floor(diffMs / 3600000);
      const diffD = Math.floor(diffMs / 86400000);
      if (diffMin < 1) return 'Agora';
      if (diffMin < 60) return `${diffMin}min atrás`;
      if (diffH < 24) return `${diffH}h atrás`;
      if (diffD < 7) return `${diffD}d atrás`;
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    } catch { return ''; }
  };

  const formatChatTime = (dateStr) => {
    try { return new Date(dateStr).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
  };

  // ============================================================
  // MEDIA UPLOAD INLINE (Áudio e Imagem para listas CRM)
  // ============================================================

  const getAudioMimeType = () => {
    try {
      const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/ogg'];
      for (const c of candidates) {
        if (window.MediaRecorder && MediaRecorder.isTypeSupported?.(c)) return c;
      }
    } catch (_) {}
    return '';
  };

  const startInlineRecording = useCallback((key) => {
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mimeType = getAudioMimeType();
        const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
        audioChunksRefs.current[key] = [];
        mr.ondataavailable = (e) => {
          if (e.data?.size > 0) audioChunksRefs.current[key]?.push(e.data);
        };
        mr.onstop = () => {
          try { stream.getTracks().forEach(t => t.stop()); } catch (_) {}
          const chunks = audioChunksRefs.current[key] || [];
          if (chunks.length === 0) return;
          const blob = new Blob(chunks, { type: mr.mimeType || 'audio/webm' });
          const previewUrl = URL.createObjectURL(blob);
          setActiveRecorders(prev => ({ ...prev, [key]: { ...prev[key], status: 'recorded', blob, previewUrl } }));
        };
        mediaRecorderRefs.current[key] = mr;
        mr.start();
        setActiveRecorders(prev => ({ ...prev, [key]: { ...prev[key], status: 'recording' } }));
      } catch (err) {
        console.error('[InlineRecorder] Erro ao iniciar gravação:', err);
        alert('Erro ao acessar microfone. Verifique as permissões do navegador.');
      }
    })();
  }, []);

  const stopInlineRecording = useCallback((key) => {
    const mr = mediaRecorderRefs.current[key];
    if (!mr || mr.state === 'inactive') return;
    mr.stop();
    mediaRecorderRefs.current[key] = null;
  }, []);

  const confirmInlineAudio = useCallback(async (key, listId, projectId) => {
    const rec = activeRecorders[key];
    if (!rec?.blob) return;
    setActiveRecorders(prev => ({ ...prev, [key]: { ...prev[key], status: 'uploading' } }));
    try {
      const userData = JSON.parse(localStorage.getItem('astro_user_data') || '{}');
      const companyId = userData?.company_id;
      if (!companyId) throw new Error('Empresa não identificada');
      const bucket = 'project-attachments';
      const ext = (rec.blob.type || '').includes('ogg') ? 'ogg' : 'webm';
      const fileName = `crm-initial-audio/${companyId}/${projectId}/${listId}/${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage.from(bucket).upload(fileName, rec.blob, {
        cacheControl: '3600', upsert: true, contentType: rec.blob.type || 'audio/webm'
      });
      if (uploadError) throw new Error(uploadError.message);
      const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(fileName);
      const publicUrl = urlData?.publicUrl;
      if (!publicUrl) throw new Error('Falha ao obter URL pública');
      // Salvar na metadata da lista
      const { data: lista } = await supabase.from('project_lists').select('metadata').eq('id', listId).single();
      const metadata = lista?.metadata || {};
      metadata.initial_audio_enabled = true;
      metadata.initial_audio_url = publicUrl;
      if (metadata.initial_audio_block_agent_response === undefined) metadata.initial_audio_block_agent_response = true;
      await supabase.from('project_lists').update({ metadata, updated_at: new Date().toISOString() }).eq('id', listId);
      setActiveRecorders(prev => ({ ...prev, [key]: { ...prev[key], status: 'done', publicUrl } }));
      // Enviar confirmação ao agente
      const confirmMsg = `✅ Áudio gravado e salvo com sucesso na lista! A ferramenta de áudio foi habilitada automaticamente.`;
      setChatMessages(prev => [...prev, { role: 'user', content: confirmMsg, timestamp: new Date().toISOString() }]);
      // Enviar ao backend para o agente saber
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (token) {
        const resp = await fetch(`${BACKEND_NODE_URL}/api/webchat/message`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ message: confirmMsg, session_id: chatSessionId })
        });
        if (resp.ok) {
          const data = await resp.json();
          setChatMessages(prev => [...prev, { role: 'assistant', content: data.response, timestamp: data.timestamp }]);
        }
      }
    } catch (err) {
      console.error('[InlineRecorder] Erro ao salvar áudio:', err);
      alert('Erro ao salvar áudio: ' + err.message);
      setActiveRecorders(prev => ({ ...prev, [key]: { ...prev[key], status: 'recorded' } }));
    }
  }, [activeRecorders, chatSessionId]);

  const confirmInlineImage = useCallback(async (key, listId, projectId, file) => {
    if (!file) return;
    setActiveRecorders(prev => ({ ...prev, [key]: { ...prev[key], status: 'uploading' } }));
    try {
      if (file.size > 3 * 1024 * 1024) throw new Error('Imagem deve ter no máximo 3MB');
      if (!file.type.startsWith('image/')) throw new Error('Arquivo não é uma imagem válida');
      const userData = JSON.parse(localStorage.getItem('astro_user_data') || '{}');
      const companyId = userData?.company_id;
      if (!companyId) throw new Error('Empresa não identificada');
      // Converter para base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      // Salvar na metadata da lista
      const { data: lista } = await supabase.from('project_lists').select('metadata').eq('id', listId).single();
      const metadata = lista?.metadata || {};
      const images = metadata.initial_images || [];
      if (images.length >= 3) throw new Error('Máximo de 3 imagens já atingido');
      images.push(base64);
      metadata.initial_images = images;
      metadata.enabled = true;
      await supabase.from('project_lists').update({ metadata, updated_at: new Date().toISOString() }).eq('id', listId);
      setActiveRecorders(prev => ({ ...prev, [key]: { ...prev[key], status: 'done', previewUrl: base64 } }));
      // Enviar confirmação ao agente
      const confirmMsg = `✅ Imagem enviada e salva com sucesso na lista! A ferramenta de imagem foi habilitada automaticamente. (${images.length}/3 imagens)`;
      setChatMessages(prev => [...prev, { role: 'user', content: confirmMsg, timestamp: new Date().toISOString() }]);
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (token) {
        const resp = await fetch(`${BACKEND_NODE_URL}/api/webchat/message`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ message: confirmMsg, session_id: chatSessionId })
        });
        if (resp.ok) {
          const data = await resp.json();
          setChatMessages(prev => [...prev, { role: 'assistant', content: data.response, timestamp: data.timestamp }]);
        }
      }
    } catch (err) {
      console.error('[InlineUploader] Erro ao salvar imagem:', err);
      alert('Erro ao salvar imagem: ' + err.message);
      setActiveRecorders(prev => ({ ...prev, [key]: { ...prev[key], status: 'idle' } }));
    }
  }, [activeRecorders, chatSessionId]);

  const confirmInlineProductImage = useCallback(async (key, productId, file) => {
    if (!file) return;
    setActiveRecorders(prev => ({ ...prev, [key]: { ...prev[key], status: 'uploading' } }));
    try {
      if (file.size > 5 * 1024 * 1024) throw new Error('Imagem deve ter no máximo 5MB');
      if (!file.type.startsWith('image/')) throw new Error('Arquivo não é uma imagem válida');
      const userData = JSON.parse(localStorage.getItem('astro_user_data') || '{}');
      const companyId = userData?.company_id;
      if (!companyId) throw new Error('Empresa não identificada');
      // Upload para Supabase Storage
      const ext = file.name.split('.').pop() || 'jpg';
      const filePath = `${companyId}/produtos/${productId}/main.${ext}`;
      const { error: uploadErr } = await supabase.storage.from('products-images').upload(filePath, file, { upsert: true, contentType: file.type });
      if (uploadErr) throw new Error(`Erro no upload: ${uploadErr.message}`);
      const { data: urlData } = supabase.storage.from('products-images').getPublicUrl(filePath);
      const publicUrl = urlData?.publicUrl;
      if (!publicUrl) throw new Error('Não foi possível obter URL pública');
      // Atualizar produto no banco
      await supabase.from('produtos_empresa').update({ imagem_principal: publicUrl, updated_at: new Date().toISOString() }).eq('id', productId).eq('company_id', companyId);
      const preview = URL.createObjectURL(file);
      setActiveRecorders(prev => ({ ...prev, [key]: { ...prev[key], status: 'done', previewUrl: preview } }));
      // Confirmar ao agente
      const confirmMsg = `✅ Foto do produto enviada e salva com sucesso!`;
      setChatMessages(prev => [...prev, { role: 'user', content: confirmMsg, timestamp: new Date().toISOString() }]);
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (token) {
        const resp = await fetch(`${BACKEND_NODE_URL}/api/webchat/message`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ message: confirmMsg, session_id: chatSessionId })
        });
        if (resp.ok) {
          const data = await resp.json();
          setChatMessages(prev => [...prev, { role: 'assistant', content: data.response, timestamp: data.timestamp }]);
        }
      }
    } catch (err) {
      console.error('[ProductImageUploader] Erro:', err);
      alert('Erro ao salvar imagem: ' + err.message);
      setActiveRecorders(prev => ({ ...prev, [key]: { ...prev[key], status: 'idle' } }));
    }
  }, [activeRecorders, chatSessionId]);

  // Buscar QR code do banco quando marcador é encontrado
  const fetchQrCode = useCallback(async (connectionId) => {
    if (qrCodes[connectionId]) return; // já carregado
    try {
      setQrCodes(prev => ({ ...prev, [connectionId]: { loading: true } }));
      const { data, error } = await supabase
        .from('conexoes_uazapi')
        .select('qrcode_dataurl')
        .eq('connection_id', connectionId)
        .single();
      if (error || !data?.qrcode_dataurl) {
        setQrCodes(prev => ({ ...prev, [connectionId]: { loading: false, error: true } }));
        return;
      }
      setQrCodes(prev => ({ ...prev, [connectionId]: { loading: false, dataUrl: data.qrcode_dataurl } }));
    } catch (e) {
      console.error('[fetchQrCode] Erro:', e);
      setQrCodes(prev => ({ ...prev, [connectionId]: { loading: false, error: true } }));
    }
  }, [qrCodes]);

  // Limpar object URLs ao desmontar
  useEffect(() => {
    return () => {
      Object.values(activeRecorders).forEach(r => {
        if (r?.previewUrl?.startsWith('blob:')) {
          try { URL.revokeObjectURL(r.previewUrl); } catch (_) {}
        }
      });
    };
  }, []);

  // ============================================================
  // RENDERIZAÇÃO DE MENSAGENS
  // ============================================================

  const renderMarkdown = (text) => {
    if (!text) return '';
    // Remover marcadores de mídia (serão renderizados como componentes React)
    let cleaned = text
      .replace(/\{\{AUDIO_RECORDER:[^}]+\}\}/g, '')
      .replace(/\{\{IMAGE_UPLOADER:[^}]+\}\}/g, '')
      .replace(/\{\{PRODUCT_IMAGE_UPLOADER:[^}]+\}\}/g, '')
      .replace(/\{\{QRCODE_IMAGE:[^}]+\}\}/g, '');
    return cleaned
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      .replace(/\n- /g, '\n• ')
      .replace(/\n(\d+)\. /g, '\n$1. ');
  };

  // Extrair marcadores de mídia de uma mensagem
  const extractMediaMarkers = (text) => {
    if (!text) return [];
    const markers = [];
    const audioRegex = /\{\{AUDIO_RECORDER:([^:]+):([^}]+)\}\}/g;
    const imageRegex = /\{\{IMAGE_UPLOADER:([^:]+):([^}]+)\}\}/g;
    const productImageRegex = /\{\{PRODUCT_IMAGE_UPLOADER:([^}]+)\}\}/g;
    const qrcodeRegex = /\{\{QRCODE_IMAGE:([^}]+)\}\}/g;
    let match;
    while ((match = audioRegex.exec(text)) !== null) {
      markers.push({ type: 'audio', listId: match[1], projectId: match[2] });
    }
    while ((match = imageRegex.exec(text)) !== null) {
      markers.push({ type: 'image', listId: match[1], projectId: match[2] });
    }
    while ((match = productImageRegex.exec(text)) !== null) {
      markers.push({ type: 'product_image', productId: match[1] });
    }
    while ((match = qrcodeRegex.exec(text)) !== null) {
      markers.push({ type: 'qrcode', connectionId: match[1] });
    }
    return markers;
  };

  const userInitial = displayName ? displayName.charAt(0).toUpperCase() : 'U';
  
  if (initialLoading) {
    return (
      <div className="dashboard-page">
        <LoadingSpinner message="Carregando dashboard..." />
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <TextType 
          text={["Bem vindo a HALO IA", "O Futuro dos Agentes Inteligentes"]}
          typingSpeed={75}
          pauseDuration={1500}
          deletingSpeed={50}
          cursorBlinkDuration={0.5}
          showCursor={true}
          cursorCharacter="|"
          textColors={["#e0e0e0"]}
          variableSpeed={{min: 60, max: 120}}
          className="dashboard-header-text"
        />
      </div>
      
      <div className="dashboard-content">
        {/* Card de Boas-vindas */}
        <div className="welcome-card">
          <div className="welcome-content">
            <h2>Olá, {displayName}!</h2>
            <div className="widget-placeholder">
              <WeatherCard />
              <FxCard />
            </div>
          </div>
        </div>

        {/* Card de Chat com Agente Halo */}
        <div className="dash-chat-card">
          {/* Header */}
          <div className="dash-chat-header">
            <div className="dash-chat-header-left">
              <div className="dash-chat-avatar">
                <img src="/assets/images/3.webp" alt="Halo" />
              </div>
              <div>
                <div className="dash-chat-title">Halo</div>
              </div>
            </div>
            <div className="dash-chat-header-actions">
              <div className="dash-chat-history-wrapper">
                <button className="dash-chat-new-btn" onClick={toggleSessions} title="Conversas anteriores">
                  <AppIcon name="history" style={{ fontSize: 18 }} />
                </button>
                {showSessions && (
                  <div className="dash-chat-sessions-panel">
                    <div className="dash-chat-sessions-header">
                      <span>Conversas anteriores</span>
                      <button className="dash-chat-sessions-close" onClick={() => setShowSessions(false)}>
                        <AppIcon name="close" style={{ fontSize: 16 }} />
                      </button>
                    </div>
                    <div
                      className="dash-chat-sessions-list"
                      ref={sessionsListRef}
                      onScroll={handleSessionsScroll}
                    >
                      {sessions.length === 0 && !sessionsLoading && (
                        <div className="dash-chat-sessions-empty">
                          Nenhuma conversa anterior
                        </div>
                      )}
                      {sessions.map((s) => (
                        <button
                          key={s.session_id}
                          className={`dash-chat-session-item ${s.session_id === chatSessionId ? 'active' : ''}`}
                          onClick={() => switchToSession(s.session_id)}
                        >
                          <div className="dash-chat-session-preview">
                            {s.preview || 'Conversa sem mensagens'}
                          </div>
                          <div className="dash-chat-session-meta">
                            <span>{s.message_count} msg{s.message_count !== 1 ? 's' : ''}</span>
                            <span>{formatRelativeDate(s.last_activity)}</span>
                          </div>
                        </button>
                      ))}
                      {sessionsLoading && (
                        <div className="dash-chat-sessions-loading">
                          <div className="dash-chat-typing-dots" style={{ justifyContent: 'center' }}>
                            <span></span><span></span><span></span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <button className="dash-chat-new-btn" onClick={handleNewChatSession} title="Nova conversa">
                <AppIcon name="add" style={{ fontSize: 18 }} />
              </button>
            </div>
          </div>

          {/* Mensagens */}
          <div className="dash-chat-messages">
            {chatMessages.length === 0 && !chatLoading && (
              <div className="dash-chat-welcome">
                <div className="dash-chat-welcome-icon">
                  <img src="/assets/images/3.webp" alt="Halo" />
                </div>
                <h3>Olá, {displayName}!</h3>
                <p>Sou o Halo, seu assistente inteligente. Posso ajudar com configurações, consultar dados, ver métricas e muito mais.</p>
                <div className="dash-chat-suggestions">
                  {(() => {
                    const allSuggestions = [
                      // Criação de Agentes
                      'Quero criar um novo agente de IA',
                      'Me ajude a configurar um agente de atendimento',
                      // Conexões
                      'Quero conectar meu WhatsApp ao sistema',
                      'Quais conexões WhatsApp tenho ativas?',
                      'Vincular um agente a uma conexão WhatsApp',
                      // Projetos CRM
                      'Quero criar um novo projeto CRM',
                      'Configurar um projeto Kanban',
                      'Quais projetos eu tenho?',
                      // Listas e Ferramentas CRM
                      'Adicionar uma mensagem de boas-vindas automática',
                      'Configurar um áudio automático em uma lista',
                      'Adicionar botões de link em uma lista CRM',
                      'Criar uma nova lista em um projeto',
                      'Configurar follow-up automático',
                      // Agentes
                      'Quais agentes de IA eu tenho?',
                      'Alterar a temperatura de um agente',
                      'Alterar o nome de um agente',
                      // Métricas e Dados
                      'Quais são minhas métricas de atendimento?',
                      'Quantos contatos eu tenho?',
                      'Ver minhas conversas recentes',
                      // Empresa
                      'Ver informações da minha empresa',
                      'Atualizar dados da minha empresa',
                      // Documentação
                      'Como funciona o sistema CRM?',
                      'O que o Halo pode fazer por mim?',
                      'Como funciona a distribuição de leads?',
                    ];
                    const shuffled = [...allSuggestions].sort(() => Math.random() - 0.5);
                    return shuffled.slice(0, 6).map((s, i) => (
                      <button key={i} className="dash-chat-suggestion" onClick={() => sendSuggestion(s)}>
                        {s}
                      </button>
                    ));
                  })()}
                </div>
              </div>
            )}

            {chatMessages.map((msg, i) => {
              const mediaMarkers = msg.role === 'assistant' ? extractMediaMarkers(msg.content) : [];
              return (
                <div key={i} className={`dash-chat-msg ${msg.role}`}>
                  <div className="dash-chat-msg-avatar">
                    {msg.role === 'assistant' ? (
                      <img src="/assets/images/3.webp" alt="Halo" />
                    ) : (
                      userInitial
                    )}
                  </div>
                  <div className="dash-chat-msg-content">
                    <div
                      className="dash-chat-msg-bubble"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(msg.role === 'assistant' ? renderMarkdown(msg.content) : msg.content)
                      }}
                    />
                    {/* Componentes inline de mídia */}
                    {mediaMarkers.map((marker, mi) => {
                      const key = `${marker.type}-${marker.listId}-${i}`;
                      const rec = activeRecorders[key] || { status: 'idle' };

                      if (marker.type === 'audio') {
                        return (
                          <div key={mi} className="dash-inline-media-widget">
                            <div className="dash-inline-media-header">
                              <AppIcon name="mic" />
                              <span>Gravador de Áudio</span>
                            </div>
                            <div className="dash-inline-media-body">
                              {rec.status === 'done' ? (
                                <div className="dash-inline-media-done">
                                  <AppIcon name="check_circle" style={{ color: '#10b981', fontSize: 28 }} />
                                  <span>Áudio salvo com sucesso!</span>
                                </div>
                              ) : rec.status === 'uploading' ? (
                                <div className="dash-inline-media-uploading">
                                  <div className="dash-chat-typing-dots"><span></span><span></span><span></span></div>
                                  <span>Salvando áudio...</span>
                                </div>
                              ) : (
                                <>
                                  {(rec.previewUrl) && (
                                    <audio controls src={rec.previewUrl} style={{ width: '100%', marginBottom: 8 }} />
                                  )}
                                  <div className="dash-inline-media-actions">
                                    {rec.status === 'recording' ? (
                                      <button className="dash-inline-btn recording" onClick={() => stopInlineRecording(key)}>
                                        <AppIcon name="stop" />
                                        Parar Gravação
                                      </button>
                                    ) : (
                                      <button className="dash-inline-btn record" onClick={() => startInlineRecording(key)}>
                                        <AppIcon name="mic" />
                                        {rec.status === 'recorded' ? 'Regravar' : 'Gravar Áudio'}
                                      </button>
                                    )}
                                    {rec.status === 'recorded' && (
                                      <button className="dash-inline-btn confirm" onClick={() => confirmInlineAudio(key, marker.listId, marker.projectId)}>
                                        <AppIcon name="check" />
                                        Confirmar e Salvar
                                      </button>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      }

                      if (marker.type === 'image') {
                        return (
                          <div key={mi} className="dash-inline-media-widget">
                            <div className="dash-inline-media-header">
                              <AppIcon name="image" />
                              <span>Upload de Imagem</span>
                            </div>
                            <div className="dash-inline-media-body">
                              {rec.status === 'done' ? (
                                <div className="dash-inline-media-done">
                                  {rec.previewUrl && (
                                    <img src={rec.previewUrl} alt="Preview" style={{ maxWidth: 120, maxHeight: 80, borderRadius: 8, marginBottom: 6 }} />
                                  )}
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <AppIcon name="check_circle" style={{ color: '#10b981', fontSize: 28 }} />
                                    <span>Imagem salva com sucesso!</span>
                                  </div>
                                </div>
                              ) : rec.status === 'uploading' ? (
                                <div className="dash-inline-media-uploading">
                                  <div className="dash-chat-typing-dots"><span></span><span></span><span></span></div>
                                  <span>Salvando imagem...</span>
                                </div>
                              ) : (
                                <>
                                  {rec.previewUrl && (
                                    <img src={rec.previewUrl} alt="Preview" style={{ maxWidth: 160, maxHeight: 100, borderRadius: 8, marginBottom: 8 }} />
                                  )}
                                  <div className="dash-inline-media-actions">
                                    <label className="dash-inline-btn record" style={{ cursor: 'pointer' }}>
                                      <AppIcon name="cloud_upload" />
                                      Selecionar Imagem
                                      <input
                                        type="file"
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        onChange={(e) => {
                                          const file = e.target.files?.[0];
                                          if (file) {
                                            const preview = URL.createObjectURL(file);
                                            setActiveRecorders(prev => ({ ...prev, [key]: { ...prev[key], status: 'selected', file, previewUrl: preview } }));
                                          }
                                        }}
                                      />
                                    </label>
                                    {rec.status === 'selected' && rec.file && (
                                      <button className="dash-inline-btn confirm" onClick={() => confirmInlineImage(key, marker.listId, marker.projectId, rec.file)}>
                                        <AppIcon name="check" />
                                        Confirmar e Salvar
                                      </button>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      }

                      if (marker.type === 'product_image') {
                        const pKey = `product_image-${marker.productId}-${i}`;
                        const pRec = activeRecorders[pKey] || { status: 'idle' };
                        return (
                          <div key={mi} className="dash-inline-media-widget">
                            <div className="dash-inline-media-header">
                              <AppIcon name="add_photo_alternate" />
                              <span>Foto do Produto</span>
                            </div>
                            <div className="dash-inline-media-body">
                              {pRec.status === 'done' ? (
                                <div className="dash-inline-media-done">
                                  {pRec.previewUrl && (
                                    <img src={pRec.previewUrl} alt="Produto" style={{ maxWidth: 120, maxHeight: 80, borderRadius: 8, marginBottom: 6 }} />
                                  )}
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <AppIcon name="check_circle" style={{ color: '#10b981', fontSize: 28 }} />
                                    <span>Foto salva com sucesso!</span>
                                  </div>
                                </div>
                              ) : pRec.status === 'uploading' ? (
                                <div className="dash-inline-media-uploading">
                                  <div className="dash-chat-typing-dots"><span></span><span></span><span></span></div>
                                  <span>Enviando foto...</span>
                                </div>
                              ) : (
                                <>
                                  {pRec.previewUrl && (
                                    <img src={pRec.previewUrl} alt="Preview" style={{ maxWidth: 160, maxHeight: 100, borderRadius: 8, marginBottom: 8 }} />
                                  )}
                                  <div className="dash-inline-media-actions">
                                    <label className="dash-inline-btn record" style={{ cursor: 'pointer' }}>
                                      <AppIcon name="cloud_upload" />
                                      Selecionar Foto
                                      <input
                                        type="file"
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        onChange={(e) => {
                                          const file = e.target.files?.[0];
                                          if (file) {
                                            const preview = URL.createObjectURL(file);
                                            setActiveRecorders(prev => ({ ...prev, [pKey]: { status: 'selected', file, previewUrl: preview } }));
                                          }
                                        }}
                                      />
                                    </label>
                                    {pRec.status === 'selected' && pRec.file && (
                                      <button className="dash-inline-btn confirm" onClick={() => confirmInlineProductImage(pKey, marker.productId, pRec.file)}>
                                        <AppIcon name="check" />
                                        Confirmar e Salvar
                                      </button>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      }

                      if (marker.type === 'qrcode') {
                        const connId = marker.connectionId;
                        const qrState = qrCodes[connId];
                        // Disparar busca do QR code do banco se ainda não carregado
                        if (!qrState) {
                          fetchQrCode(connId);
                        }
                        return (
                          <div key={mi} className="dash-inline-media-widget">
                            <div className="dash-inline-media-header">
                              <AppIcon name="qr_code_2" />
                              <span>QR Code WhatsApp</span>
                            </div>
                            <div className="dash-inline-media-body" style={{ textAlign: 'center', padding: '12px' }}>
                              {qrState?.loading ? (
                                <div style={{ padding: '16px', color: '#9ca3af' }}>
                                  <AppIcon name="progress_activity" style={{ fontSize: 48, display: 'block', marginBottom: 8, animation: 'spin 1s linear infinite' }} />
                                  <span>Carregando QR Code...</span>
                                </div>
                              ) : qrState?.dataUrl ? (
                                <img src={qrState.dataUrl} alt="QR Code WhatsApp" style={{ maxWidth: 250, maxHeight: 250, borderRadius: 8 }} />
                              ) : (
                                <div style={{ padding: '16px', color: '#9ca3af' }}>
                                  <AppIcon name="qr_code_2" style={{ fontSize: 48, display: 'block', marginBottom: 8 }} />
                                  <span>{qrState?.error ? 'Não foi possível carregar o QR Code' : 'Escaneie o QR Code com o WhatsApp'}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      }

                      return null;
                    })}
                    <div className="dash-chat-msg-time">{formatChatTime(msg.timestamp)}</div>
                  </div>
                </div>
              );
            })}

            {chatLoading && (
              <div className="dash-chat-msg assistant">
                <div className="dash-chat-msg-avatar">
                  <img src="/assets/images/3.webp" alt="Halo" />
                </div>
                <div className="dash-chat-typing-dots">
                  <span></span><span></span><span></span>
                </div>
              </div>
            )}

            <div ref={chatEndRef} />
          </div>

          {/* Input */}
          <div className="dash-chat-input-area">
            {/* Slash Command Menu */}
            <SlashCommandMenu
              isOpen={showSlashMenu}
              onClose={() => { setShowSlashMenu(false); setSlashFilter(''); }}
              onSelectCommand={(command) => {
                setChatInput(`/${command} `);
                setShowSlashMenu(false);
                setSlashFilter('');
                setTimeout(() => chatTextareaRef.current?.focus(), 50);
              }}
              filterText={slashFilter}
            />

            {/* Botão de ferramentas */}
            <button
              className={`slash-menu-trigger ${showSlashMenu ? 'active' : ''}`}
              onClick={() => {
                setShowSlashMenu(prev => !prev);
                setSlashFilter('');
              }}
              title="Ferramentas disponíveis"
              type="button"
            >
              <AppIcon name={showSlashMenu ? 'close' : 'apps'} />
            </button>

            <textarea
              ref={chatTextareaRef}
              className="dash-chat-input"
              placeholder="Pergunte algo ao Halo... (digite / para ver ferramentas)"
              value={chatInput}
              onChange={handleChatInputChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (showSlashMenu) {
                    setShowSlashMenu(false);
                    setSlashFilter('');
                  }
                  sendChatMessage();
                }
                if (e.key === 'Escape' && showSlashMenu) {
                  setShowSlashMenu(false);
                  setSlashFilter('');
                }
              }}
              rows={1}
              disabled={chatLoading}
            />
            <button
              className="dash-chat-send-btn"
              onClick={() => {
                if (showSlashMenu) {
                  setShowSlashMenu(false);
                  setSlashFilter('');
                }
                sendChatMessage();
              }}
              disabled={!chatInput.trim() || chatLoading}
              title="Enviar"
            >
              <AppIcon name="send" style={{ fontSize: 20 }} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
